import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingState, ErrorState } from "@/components/ui/empty";
import { EntryTypeBadge } from "@/components/status-badges";
import { Markdown } from "@/components/markdown";
import { useApi } from "@/lib/api";
import { formatDate, formatRelative, parseJson } from "@/lib/format";
import type { JournalEntry } from "@/lib/types";

export function JournalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const entry = useApi<JournalEntry>(id ? `/api/journal/${id}` : null);

  if (entry.loading) return <LoadingState />;
  if (entry.error) return <ErrorState error={entry.error} />;
  if (!entry.data) return null;
  const e = entry.data;
  const tags = parseJson<string[]>(e.tags, []);

  return (
    <>
      <Link
        to="/journal"
        className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" />
        All entries
      </Link>
      <PageHeader
        title={e.title}
        description={`${e.author} · ${formatDate(e.timestamp)} (${formatRelative(e.timestamp)})`}
        actions={<EntryTypeBadge entryType={e.entry_type} />}
      />

      <div className="grid gap-6 lg:grid-cols-4">
        <div className="lg:col-span-3">
          <Card>
            <CardContent className="p-6">
              <Markdown>{e.body}</Markdown>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          {tags.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Tags</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-1">
                {tags.map((t) => (
                  <Badge
                    key={t}
                    variant="outline"
                    className="text-[10px] font-mono"
                  >
                    {t}
                  </Badge>
                ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              {e.campaign_id && (
                <LinkRow
                  label="Campaign"
                  to={`/campaigns/${e.campaign_id}`}
                  value="view"
                />
              )}
              {e.experiment_id && (
                <LinkRow
                  label="Experiment"
                  to={`/experiments/${e.experiment_id}`}
                  value="view"
                />
              )}
              {e.rung_id && (
                <Meta label="Rung" value={e.rung_id} mono />
              )}
              {e.engine_version && (
                <Meta label="Engine" value={e.engine_version} mono />
              )}
              {e.organism_id && (
                <Meta label="Organism" value={e.organism_id.slice(0, 16)} mono />
              )}
              {!e.campaign_id &&
                !e.experiment_id &&
                !e.rung_id &&
                !e.engine_version && (
                  <p className="text-muted-foreground">
                    No links attached.
                  </p>
                )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}

function LinkRow({
  label,
  to,
  value,
}: {
  label: string;
  to: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <Link to={to} className="text-primary hover:underline">
        {value}
      </Link>
    </div>
  );
}

function Meta({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? "font-mono text-right" : "text-right"}>
        {value}
      </span>
    </div>
  );
}
